import { useState } from "react";

export type AvatarTone = "amber" | "ink" | "stone" | "cognac" | "terra";

export interface AvatarProps {
	initials: string;
	src?: string | null;
	tone?: AvatarTone;
	size?: number;
	status?: "online";
	className?: string;
	style?: React.CSSProperties;
	ring?: boolean;
}

const toneColorMap: Record<AvatarTone, { bg: string; color: string }> = {
	amber: {
		bg: "color-mix(in srgb, var(--color-dashboard-accent) 12%, transparent)",
		color: "var(--color-dashboard-accent-dark)",
	},
	ink: {
		bg: "var(--color-dashboard-dark)",
		color: "var(--color-dashboard-on-fill)",
	},
	stone: {
		bg: "var(--color-dashboard-muted)",
		color: "var(--color-dashboard-on-fill)",
	},
	cognac: { bg: "#334155", color: "var(--color-dashboard-on-fill)" },
	terra: {
		bg: "var(--color-dashboard-accent-hover)",
		color: "var(--color-dashboard-on-fill)",
	},
};

export function Avatar({
	initials,
	src,
	tone = "amber",
	size = 36,
	status,
	className,
	style,
	ring = false,
}: AvatarProps) {
	const { bg, color } = toneColorMap[tone];
	const [hasError, setHasError] = useState(false);

	const showImage = src && !hasError;

	return (
		<div
			className={className}
			style={{
				position: "relative",
				display: "inline-flex",
				...style,
			}}
		>
			<div
				style={{
					width: size,
					height: size,
					borderRadius: "50%",
					background: bg,
					color,
					fontFamily: "var(--font-body)",
					fontSize: size * 0.38,
					fontWeight: 600,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
					boxShadow: ring
						? `0 0 0 2px var(--color-dashboard-paper)`
						: undefined,
				}}
			>
				{showImage ? (
					<img
						src={src}
						alt={initials}
						onError={() => setHasError(true)}
						style={{
							width: "100%",
							height: "100%",
							objectFit: "cover",
						}}
					/>
				) : (
					initials
				)}
			</div>
			{status === "online" && (
				<span
					style={{
						position: "absolute",
						bottom: 0,
						right: 0,
						width: size * 0.28,
						height: size * 0.28,
						borderRadius: "50%",
						background: "var(--color-dashboard-success-fill)",
						border: "2px solid var(--color-dashboard-paper)",
					}}
				/>
			)}
		</div>
	);
}
